import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { PanelChromeProvider, useIsMobile } from './PanelChromeContext';

export type PanelType = 'tenant' | 'customer' | 'admin';
export type PanelDensity = 'compact' | 'default' | 'comfort';

export interface PanelFrameProps {
  panel?: PanelType;
  density?: PanelDensity;
  sidebar: ReactNode;
  topbar: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}

/**
 * Full-screen frame: sidebar (232) + main column (topbar + content).
 * `dg-root` scopes Inter + box-sizing reset; density variants apply to row tokens.
 * <1024px: sidebar fixed drawer'a dönüşür (PanelChromeContext üzerinden
 * Topbar hamburger'ı ve Sidebar item'ları drawer'ı yönetir).
 */
export function PanelFrame({
  panel = 'tenant',
  density = 'default',
  sidebar,
  topbar,
  children,
  style,
}: PanelFrameProps) {
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  // Viewport breakpoint üstüne büyürse (rotasyon/resize) drawer state'i sıfırla.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  // Escape drawer'ı kapatır.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const densityCls =
    density === 'compact'
      ? 'dg-density-compact'
      : density === 'comfort'
      ? 'dg-density-comfort'
      : '';
  const mainBg = panel === 'customer' ? 'var(--dg-surface)' : 'white';

  return (
    <PanelChromeProvider value={{ isMobile, navOpen, setNavOpen }}>
      <div
        className={`dg-root ${densityCls}`.trim()}
        style={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          background: 'var(--dg-bg)',
          overflow: 'hidden',
          ...style,
        }}
      >
        {isMobile ? (
          <>
            {navOpen && (
              <div
                role="presentation"
                aria-hidden="true"
                data-testid="dg-nav-backdrop"
                onClick={() => setNavOpen(false)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(15, 23, 42, 0.5)',
                  zIndex: 40, // DS Modal overlay (50) altında kalır
                }}
              />
            )}
            <div
              data-testid="dg-nav-drawer"
              style={{
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                width: 280,
                maxWidth: '85vw',
                zIndex: 41,
                display: 'flex',
                transform: navOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.2s ease',
              }}
            >
              {sidebar}
            </div>
          </>
        ) : (
          sidebar
        )}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {topbar}
          <main style={{ flex: 1, overflow: 'auto', background: mainBg }}>{children}</main>
        </div>
      </div>
    </PanelChromeProvider>
  );
}
